import { InfoCircleFilled, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Modal,
  Spin,
  Empty,
  Col,
  Button,
  Divider,
  message,
  ConfigProvider,
  Alert,
  Typography,
  Flex,
  Tooltip
} from "antd";
import { useState } from "react";
import classes from "./Jobs.module.css";
import { getHistogramData, getJobRunsById, getJobDataById } from "api/job";
import ApexCharts from "react-apexcharts";
import JobDownloadButton from "./JobDownload";
import apiHelper from "api/apiHelper";
import { MESSAGES } from "constants/strings";
import { QUIX_SALES_EMAIL } from "constants/common";
import { COLOR_PRIMARY_ORANGE } from "theme/colors";

export const ViewJob = ({
  jobName,
  jobId,
  elapsedTime
}: {
  jobName: string;
  jobId: string;
  elapsedTime: number;
}) => {
  const [isJobViewModalOpen, setIsJobViewModalOpen] = useState(false);
  const [jobRuns, setJobRuns] = useState<any>([]);
  const [jobRunsLoading, setJobRunsLoading] = useState(false);
  const [histogramData, setHistogramData] = useState<any>([]);
  const [histogramDataLoading, setHistogramDataLoading] = useState(false);
  const [jobData, setJobData] = useState<any>({});

  const fetchJobRuns = async () => {
    try {
      setJobRunsLoading(true);
      const jobRuns = await getJobRunsById(jobId);
      const jobAPIData = await getJobDataById(jobId);
      const {
        job_type,
        job_name,
        n_photons,
        n_modes,
        unitary,
        max_time_ms,
        max_events,
        configurations
      } = jobAPIData;
      if (job_type === "CU") {
        setJobData({ job_type, job_name, unitary, configurations });
      } else {
        setJobData({
          job_type,
          job_name,
          n_photons,
          n_modes,
          unitary,
          max_time_ms,
          max_events
        });
      }
      // convert array into objects with id as key
      if (jobRuns && jobRuns?.length > 0) {
        const jobRunsObj = jobRuns.reduce((acc: any, curr: any) => {
          acc[curr.id] = curr;
          return acc;
        }, {});

        setJobRuns(jobRunsObj);
        setJobRunsLoading(false);

        await fetchHistogramData(jobRunsObj);
      } else {
        setJobRuns([]);
        setJobRunsLoading(false);
      }
    } catch (err) {}
  };

  const fetchHistogramData = async (runs: any) => {
    try {
      setHistogramDataLoading(true);
      const histogramDataOfAllRuns = await getHistogramData(jobId);
      // setHistogramData(
      //     histogramDataOfAllRuns
      //         .filter((item: any) => !item.value.error)
      //         .map((item: any) => item.value.data)
      //         .flat()
      // );
      setHistogramData(histogramDataOfAllRuns.data);
      setHistogramDataLoading(false);
      if (!histogramDataOfAllRuns?.data?.length) {
        message.error("The Job is corrupt! The issue has been reported to admins.");
        setIsJobViewModalOpen(false);
      }
    } catch (err) {}
  };

  return (
    <>
      <div
        onClick={async () => {
          setHistogramData([]);
          setJobRuns([]);
          setIsJobViewModalOpen(true);
          await fetchJobRuns();
        }}
        style={{
          color: "#FF5A17",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer"
        }}>
        View
      </div>
      <ConfigProvider
        theme={{
          token: {
            colorBgBase: "#1A1D38"
          },
          components: {
            Modal: {
              contentBg: "#1A1D38"
            }
          }
        }}>
        <Modal
          footer={null}
          closable={true}
          onCancel={() => {
            setIsJobViewModalOpen(false);
          }}
          open={isJobViewModalOpen}
          centered
          title={"View Job"}
          width={1200}>
          {jobRunsLoading || histogramDataLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%"
              }}>
              <Spin
                spinning
                size="large"
                indicator={
                  <LoadingOutlined
                    style={{
                      fontSize: 48,
                      color: "white"
                    }}
                    spin
                  />
                }
              />
            </div>
          ) : histogramData?.length && histogramData?.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%"
              }}>
              <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            (histogramData || []).map((item: any, index: number) => (
              <div style={{ display: "flex", flexWrap: "wrap-reverse" }}>
                <div
                  style={{
                    color: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    gap: 12,
                    fontFamily: "Tillium Web"
                  }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div>Job Name: {jobName}</div>
                    {item.configurationName !== "" && (
                      <div>Configuration: {item.configurationName}</div>
                    )}
                    <div>Elapsed time: {(Number(elapsedTime) / 60).toFixed(2)} minutes</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Button
                      onClick={async () => {
                        const [jobId, runId] = item?.dataId?.split("/runs/") || [
                          undefined,
                          undefined
                        ];
                        if (!jobId || !runId) {
                          message.error("No result found");
                          return;
                        }
                        try {
                          const res = await apiHelper.get(`/jobs/${jobId}/runs/${runId}`, {});

                          window.open(res.data.data, "_blank");
                        } catch (er) {
                          message.error(MESSAGES.ERROR_DOWNLOAD_RESULT);
                          // console.log(er);
                        }
                      }}
                      className={classes.JVDownloadButton}>
                      Download Output
                    </Button>
                    <JobDownloadButton data={jobData} filename={jobId} />
                  </div>
                </div>
                <div style={{ width: "100%" }}>
                  <Flex
                    align="center"
                    justify="right"
                    style={{
                      width: "100%"
                    }}>
                    <Tooltip
                      placement="bottom"
                      title="This is the number of n-photons coincidences over a certain group of
                          outputs normalized to all n-photons coincidences events registered during
                          the run time.">
                      <Typography.Text style={{ marginRight: "8px", display: "flex" }}>
                        Relative frequency {"  "}
                        <span
                          style={{
                            marginLeft: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer"
                          }}>
                          <InfoCircleOutlined
                            style={{
                              color: COLOR_PRIMARY_ORANGE,
                              fontSize: "16px",
                              margin: "auto 0"
                            }}
                          />
                        </span>
                      </Typography.Text>
                    </Tooltip>
                  </Flex>
                  <ApexCharts
                    type="bar"
                    height={350}
                    width={"100%"}
                    series={[
                      {
                        data: (item?.graphPoints || (item instanceof Array ? item : [])).map(
                          (i: any) => i.relativeFrequency
                        )
                      }
                    ]}
                    options={{
                      chart: {
                        type: "bar",
                        height: 350,
                        toolbar: {
                          show: false
                        }
                      },
                      plotOptions: {
                        bar: {
                          horizontal: false,
                          columnWidth: "55%"
                        }
                      },
                      dataLabels: {
                        enabled: false
                      },
                      stroke: {
                        show: true,
                        width: 2,
                        colors: ["transparent"]
                      },
                      xaxis: {
                        title: {
                          text: "Modes in which photons were detected",
                          offsetY: 20,
                          style: {
                            color: "#fff",
                            fontFamily: "Tillium Web",
                            cssClass: classes.xAxisTitle
                          }
                        },
                        categories: (item.graphPoints || []).map((i: any) => i.labels),
                        labels: {
                          style: {
                            colors: "#fff",
                            fontFamily: "Tillium Web"
                          }
                        }
                      },
                      yaxis: {
                        title: {
                          text: "Relative frequency",
                          style: {
                            color: "#fff",
                            fontFamily: "Tillium Web"
                          }
                        },
                        labels: {
                          style: {
                            colors: "#fff",
                            fontFamily: "Tillium Web"
                          },
                          formatter: function (val: any) {
                            return Number(val).toFixed(4);
                          }
                        }
                      },
                      fill: {
                        opacity: 1,
                        colors: ["#e75012"]
                      },
                      grid: {
                        padding: {
                          bottom: 4
                        }
                      },
                      tooltip: {
                        theme: "dark",
                        y: {
                          formatter: function (val) {
                            return `${val}`;
                          }
                        }
                      }
                    }}
                  />
                </div>
                {histogramData.length > 1 && histogramData.length - 1 !== index && (
                  <Col span={24}>
                    <Divider
                      style={{
                        backgroundColor: "#ef8603",
                        margin: "20px 0"
                      }}
                    />
                  </Col>
                )}
              </div>
            ))
          )}

          <Flex
            align="center"
            justify="center"
            style={{
              width: "100%",
              marginTop: 20
            }}>
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleFilled style={{ color: "#E75012", fontSize: 16 }} />}
              message={
                <Typography.Text>
                  If you see unexpected results please contact us at{" "}
                  <Typography.Link href={`mailto:${QUIX_SALES_EMAIL}`}>
                    {QUIX_SALES_EMAIL}
                  </Typography.Link>
                </Typography.Text>
              }
            />
          </Flex>
        </Modal>
      </ConfigProvider>
    </>
  );
};
